import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import ContainerBox from "../../../../components/shared/boxes/ContainerBox";
import InvoicesConfigBasicData from "./FiscalConfigBasicData";
import InvoicesConfigTaxReform from "./FiscalConfigTaxReform";
import InvoiceConfigTaxData from "./TaxData/FiscalConfigTaxData";
import { useEffect, useRef, useState } from "react";
import HeaderBox from "../../../../components/shared/boxes/HeaderBox";
import { createOrUpdateFiscalDetails, getFiscalDetails } from "../../../api/fiscal";
import ErrorBox from "../../../../components/shared/boxes/ErrorBox";
import { ISS_TAXATION_MAP } from "../../../helpers/nfseHelpers";
import SuccessAlertSnackbar from "../../../../components/shared/snackbars/SuccessAlertSnackbar";

const FiscalConfigContainer = () => {
    const [validations, setValidations] = useState({
        basic: false,
        tax: false,
        reform: true,
    });
    const [createOrUpdateSettingsLoading, setCreateOrUpdateSettingsLoading] = useState(false);
    const [configApiError, setConfigApiError] = useState(null);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [data, setData] = useState({
        name: '',
        serviceCode: '',
        cnae: '',
        nbs: '',
        issIncidenceCity: '',
        serviceLocation: '',
        issTaxationType: 6,
        issRetention: false,
        issRate: null,
        pisCofinsTaxSituationCode: '01',
        pisCofinsRetentionCode: '0',
        pisRetentionRate: null,
        cofinsRetentionRate: null,
        csllRetentionRate: null,
        inssRetentionRate: null,
        irrfRetentionRate: null,
        cppRetentionRate: null,
        sendIbsCbs: false,
        ibsCbsTaxSituationCode: '',
        ibsCbsTaxClassification: '',
        ibsCbsOperationIndicator: '',
        ibsMunicipalRate: null,
        ibsStateRate: null,
        cbsRate: null,
    });

    const hasChangesRef = useRef(false);

    const updateData = (newData) => {
        hasChangesRef.current = true;
        setData(newData);
    };

    const handleValidityChange = (section, isValid) => {
        setValidations(prev => ({
            ...prev,
            [section]: isValid
        }));
    };

    const isFormValid = Object.values(validations).every(Boolean);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const result = await getFiscalDetails();

                const config = result?.results?.[0];
                if (!config) return;

                const mapIncidenceCityFromApi = (value) =>
                    value === 'customer_city' ? 'client_address' : 'company_address';

                const mapServiceLocationFromApi = (value) =>
                    value === 'company_location' ? 'company_address' : 'client_address';

                setData({
                    name: config.name || '',
                    serviceCode: config.service_code || '',
                    cnae: config.cnae,
                    nbs: config.nbs || '',
                    issIncidenceCity: mapIncidenceCityFromApi(config.iss_incidence_city),
                    serviceLocation: mapServiceLocationFromApi(config.service_location),
                    issTaxationType: ISS_TAXATION_MAP[config.iss_taxation_type] ?? 6,
                    issRetention: config.iss_retention ?? false,
                    issRate: config.iss_rate ? Number(config.iss_rate) : null,
                    pisCofinsTaxSituationCode: config.pis_cofins_tax_situation_code || '01',
                    pisCofinsRetentionCode: config.pis_cofins_retention_code || '0',
                    pisRetentionRate: config.pis_retention_rate ?? null,
                    cofinsRetentionRate: config.cofins_retention_rate ?? null,
                    csllRetentionRate: config.csll_retention_rate ?? null,
                    inssRetentionRate: config.inss_retention_rate ?? null,
                    irrfRetentionRate: config.irrf_retention_rate ?? null,
                    cppRetentionRate: config.cpp_retention_rate ?? null,
                    sendIbsCbs: config.send_ibs_cbs ?? false,
                    ibsCbsTaxSituationCode: config.ibs_cbs_tax_situation_code || '',
                    ibsCbsTaxClassification: config.ibs_cbs_tax_classification || '',
                    ibsCbsOperationIndicator: config.ibs_cbs_operation_indicator || '',
                    ibsMunicipalRate: config.ibs_municipal_rate ?? null,
                    ibsStateRate: config.ibs_state_rate ?? null,
                    cbsRate: config.cbs_rate ?? null,
                });

                hasChangesRef.current = false;

            } catch (error) {
                setConfigApiError({ errorMessage: error.message });
            }
        };

        fetchSettings();
    }, []);

    const handleFinish = async () => {
        const mapLocationToApi = (value) =>
            value === 'company_address' ? 0 : 1;

        setCreateOrUpdateSettingsLoading(true)
        const body = {
            fiscal_service_config: {
                name: data.name,
                service_code: data.serviceCode,
                cnae: data.cnae,
                nbs: data.nbs,
                iss_incidence_city: mapLocationToApi(data.issIncidenceCity),
                service_location: mapLocationToApi(data.serviceLocation),
                iss_taxation_type: data.issTaxationType,
                iss_retention: data.issRetention,
                iss_rate: data.issRate,
                pis_cofins_tax_situation_code: data.pisCofinsTaxSituationCode,
                pis_cofins_retention_code: data.pisCofinsRetentionCode,
                pis_retention_rate: data.pisRetentionRate,
                cofins_retention_rate: data.cofinsRetentionRate,
                csll_retention_rate: data.csllRetentionRate,
                inss_retention_rate: data.inssRetentionRate,
                irrf_retention_rate: data.irrfRetentionRate,
                cpp_retention_rate: data.cppRetentionRate,
                send_ibs_cbs: data.sendIbsCbs,
                ibs_cbs_tax_situation_code: data.ibsCbsTaxSituationCode,
                ibs_cbs_tax_classification: data.ibsCbsTaxClassification,
                ibs_cbs_operation_indicator: data.ibsCbsOperationIndicator,
                ibs_municipal_rate: data.ibsMunicipalRate,
                ibs_state_rate: data.ibsStateRate,
                cbs_rate: data.cbsRate,
            }
        };
        try {
            await createOrUpdateFiscalDetails({ body });
            hasChangesRef.current = false;
            setSuccessSnackbarOpen(true);
        } catch (error) {
            setConfigApiError({ errorMessage: error.message });
        }
        finally {
            setCreateOrUpdateSettingsLoading(false)
        }
    };

    const isSaveDisabled = !isFormValid || !hasChangesRef.current;

    return (
        <>
            <Box sx={{ bgcolor: 'background.header' }}>
                <HeaderBox>
                    <Typography variant="h3">Configurações fiscais de Serviço (NFS-e)</Typography>
                </HeaderBox>
            </Box>
            <Divider />
            <Box sx={{ bgcolor: 'background.default' }}>
                <ContainerBox sx={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                    {configApiError && (
                        <ErrorBox
                            errorMessage={configApiError.errorMessage}
                            onClose={() => {
                                setConfigApiError(null);
                            }}
                        />
                    )}
                    <InvoicesConfigBasicData onValidityChange={(valid) => handleValidityChange('basic', valid)} data={data} setData={updateData} />
                    <Divider />
                    <InvoiceConfigTaxData onValidityChange={(valid) => handleValidityChange('tax', valid)} data={data} setData={updateData} />
                    <Divider />
                    <InvoicesConfigTaxReform onValidityChange={(valid) => handleValidityChange('reform', valid)} data={data} setData={updateData} />
                    <Box>
                        <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b> Indica um campo obrigatório </Typography>
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                        <Tooltip
                            title={ !isFormValid? "Preencha os campos obrigatórios" : !hasChangesRef.current ? "Faça alterações para continuar" : ""}
                            arrow
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    disabled={isSaveDisabled}
                                    color="primary"
                                    onClick={handleFinish}
                                    loading={createOrUpdateSettingsLoading}
                                    loadingPosition="end"
                                    sx={{
                                        width: '8.3rem',
                                        '& .MuiCircularProgress-root': {
                                            marginRight: '0.5rem',
                                            marginLeft: 0,
                                        },
                                    }}
                                >
                                    Salvar
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                    <SuccessAlertSnackbar
                        open={successSnackbarOpen}
                        onClose={() => setSuccessSnackbarOpen(false)}
                        message="Configurações salvas com sucesso"
                        vertical="bottom"
                        horizontal="center" />
                </ContainerBox>
            </Box>
        </>
    );
};

export default FiscalConfigContainer;
